import type {CopilotChatModel, CopilotModel} from '@github-ui/copilot-chat-types'
import {FIRST_PARTY_VENDORS} from '@github-ui/copilot-chat-state/model-vendors'
import {useEffect, useState} from 'react'
import {fetchCAPI} from '../capi/fetch-capi'
import {getDisplayName} from './model-display-name'

// Logo URLs for different model vendors
const OPEN_AI_LOGO = '/images/modules/marketplace/models/families/openai-logo.svg'
const ANTHROPIC_LOGO = '/images/modules/marketplace/models/families/anthropic.svg'
const GEMINI_LOGO = '/images/modules/marketplace/models/families/gemini.svg'
const XAI_LOGO = '/images/modules/marketplace/models/families/xai.svg'
const XAI_LOGO_DARK = '/images/modules/marketplace/models/families/xai-dark.svg'
const MICROSOFT_LOGO = '/images/modules/marketplace/models/families/microsoft.svg'
const DEEPSEEK_LOGO = '/images/modules/marketplace/models/families/deepseek.svg'
const MOONSHOT_AI_LOGO = '/images/modules/marketplace/models/families/moonshot-ai.svg'

const LOGO_BY_VENDOR = new Map([
  ['Azure OpenAI', OPEN_AI_LOGO],
  ['OpenAI', OPEN_AI_LOGO],
  ['Anthropic', ANTHROPIC_LOGO],
  ['Google', GEMINI_LOGO],
  ['xAI', XAI_LOGO],
  ['Microsoft', MICROSOFT_LOGO],
  ['DeepSeek', DEEPSEEK_LOGO],
  ['Moonshot AI', MOONSHOT_AI_LOGO],
])

export {LOGO_BY_VENDOR}

let cachedModels: CopilotChatModel[] | null = null
let cachedDefaultModelId: string | undefined
let cachePromise: Promise<{models: CopilotChatModel[]; defaultModelId: string | undefined}> | null = null

/** Test-only: clears the module-level model cache so cases start from a clean fetch. */
export function resetAgentModelsCacheForTesting() {
  cachedModels = null
  cachedDefaultModelId = undefined
  cachePromise = null
}

export function useAgentModels(): {
  models: CopilotChatModel[]
  isLoading: boolean
  error: Error | null
  defaultModelId?: string
} {
  const [models, setModels] = useState<CopilotChatModel[]>(cachedModels || [])
  const [defaultModelId, setDefaultModelId] = useState<string | undefined>(cachedDefaultModelId)
  const [isLoading, setIsLoading] = useState(!cachedModels)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (cachedModels) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setModels(cachedModels)
      setDefaultModelId(cachedDefaultModelId)
      setIsLoading(false)
      return
    }

    if (cachePromise) {
      void (async () => {
        try {
          const result = await cachePromise
          setModels(result.models)
          setDefaultModelId(result.defaultModelId)
          setIsLoading(false)
        } catch (err) {
          setError(err instanceof Error ? err : new Error('An unexpected error occurred while loading models.'))
          setIsLoading(false)
        }
      })()
      return
    }

    async function fetchModels() {
      try {
        setIsLoading(true)
        setError(null)

        const response = await listModels()
        const data = await response.json()
        const isDarkMode = document.querySelector('[data-color-mode="dark"]') !== null
        const transformedModels = (data.data || []).map((model: CopilotModel) => {
          let logoUrl = LOGO_BY_VENDOR.get(model.vendor)
          if (model.vendor === 'xAI' && isDarkMode) {
            logoUrl = XAI_LOGO_DARK
          }

          return {
            ...model,
            displayName: getDisplayName(model.name, model.id),
            logoURL: logoUrl,
            hasLimitedCapabilities: !model.capabilities.supports.tool_calls,
            isThirdParty: !FIRST_PARTY_VENDORS.has(model.vendor),
          }
        })

        // Enterprise managed settings may specify a default model (e.g. "auto") that
        // should be pre-selected for new sessions. It is a default hint, not a lock.
        const defaultModel = typeof data.default_model === 'string' ? data.default_model : undefined

        cachedModels = transformedModels
        cachedDefaultModelId = defaultModel
        cachePromise = null

        return {models: transformedModels, defaultModelId: defaultModel}
      } catch (err) {
        cachePromise = null
        const modelsError = err instanceof Error ? err : new Error('An unexpected error occurred while loading models.')
        setError(modelsError)
        throw modelsError
      } finally {
        setIsLoading(false)
      }
    }

    if (!cachePromise) {
      cachePromise = fetchModels()
    }

    void (async () => {
      try {
        const result = await cachePromise
        setModels(result.models)
        setDefaultModelId(result.defaultModelId)
        setIsLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('An unexpected error occurred while loading models.'))
        setIsLoading(false)
      }
    })()
  }, [])

  return {models, isLoading, error, defaultModelId}
}

// Helper function that fetches agent specific models from the Jobs API
async function listModels(): Promise<Response> {
  const endpoint = '/agents/swe/models'

  return await fetchCAPI({
    path: endpoint,
    method: 'GET',
    useCapi: true,
  })
}
